// Renewal CTA + year-naming derivation — the single source for how a renewal
// presents itself on parent surfaces (RenewalCard, the seasonal Home banner,
// the wizard's renewal callout). Two rulings from the 08-19 call live here:
//
//  1. "Continue your renewal" is misleading before a renewal draft exists —
//     the CTA reads "Start your renewal" until one does.
//  2. A renewal names its year (Year 2 / Year 3); `intake.application_type`
//     ('renewal_year_2' | 'renewal_year_3', defaulted by the renewal wizard)
//     is the source of truth.
//
// Surfaces derive from this module rather than hand-rolling copy, so the two
// rulings cannot drift between the card, the banner, and the wizard.
import type { FamilyApplication } from '@/types/applications';
import type { GrantRequest } from '@/types/api';
import { signatureRenewalDue } from '@/lib/grants-status';
import { msg, type Msg } from '@/i18n/msg';
import type { CopySlotKey } from './copySlots';

export type RenewalCtaState =
  | { kind: 'none'; label: 'Start your renewal' } // no renewal yet → fresh start
  | { kind: 'editable'; label: 'Continue your renewal' }
  // #2527: staff sent the renewal back for more information. Still editable,
  // but every surface says TRF needs something and names it, instead of the
  // generic "renewal under way" a draft reads.
  | { kind: 'needs_info'; label: 'Finish your renewal' }
  | { kind: 'pending'; status: string } // submitted / under_review → status line, no button
  // DECISIONS 2026-10-11 ("clear is kind", superseding #1490's neutral line):
  // a declined renewal says plainly it was not approved, and who to ask. No button.
  | { kind: 'declined' }
  // DECISIONS 2026-10-11: a waitlisted renewal whose notice is sent says it is
  // on the waitlist and what happens next — the committee's review-again day
  // (`YYYY-MM-DD`) when it set one. No button. Before the notice the family
  // projection reads it as `under_review`, so this kind never shows early.
  | { kind: 'waitlisted' }
  // #1490 (Victor, 2026-10-05): an approved renewal that HAS its grant request
  // is a silent hand-off — the card steps aside and the family follows the
  // grant on the surfaces that already show it (/bills, Home).
  | { kind: 'handed_off' }
  | { kind: 'unknown' }; // fetch failed — assert nothing, invite nothing

const START: RenewalCtaState = { kind: 'none', label: 'Start your renewal' };

/**
 * The CTA label union as KEYS (#2109 R3-B4). `state.label` stays the stable
 * English identifier every consumer and test already matches on; a surface
 * renders `t(renewalCtaLabelKey(state))`, so the button reads in the
 * reader's language without changing the state's shape.
 */
export const RENEWAL_CTA_LABEL_KEY = {
  'Start your renewal': 'grants:renewal.cta.start',
  'Continue your renewal': 'grants:renewal.cta.continue',
  'Finish your renewal': 'grants:renewal.cta.finish',
} as const satisfies Record<Extract<RenewalCtaState, { label: string }>['label'], string>;

export function renewalCtaLabelKey(
  state: Extract<RenewalCtaState, { label: string }>,
): (typeof RENEWAL_CTA_LABEL_KEY)[keyof typeof RENEWAL_CTA_LABEL_KEY] {
  return RENEWAL_CTA_LABEL_KEY[state.label];
}

const OPEN_RENEWAL_STATUSES = new Set([
  'draft', 'submitted', 'under_review', 'needs_more_info', 'staff_recommended', 'waitlisted',
]);

/** The server's rule (backend helpers/renewalCycle.js isRenewalOpen), negated:
 *  committee-approved counts as decided only once its grant request exists. */
function isRenewalDecided(app: FamilyApplication): boolean {
  if (OPEN_RENEWAL_STATUSES.has(app.status)) return false;
  return app.status !== 'committee_approved' || Boolean(app.has_grant_request);
}

/**
 * #2659: a DECIDED renewal belongs to the grant it renewed. Once the family's
 * newest Signature grant reaches its renewal window after that decision, the
 * renewal is spent and the next year's may start. The server judges "after the
 * decision" (it holds the decision date, which a family response withholds)
 * and says so as `next_renewal_open`; the grants say the window is open now.
 * Both must agree. Without grants nothing is offered — a surface that did not
 * load them keeps the v1.143.0 behaviour (no invitation beneath a decision).
 */
function nextRenewalOpen(
  app: FamilyApplication,
  grants: GrantRequest[] | null | undefined,
  now: Date | undefined,
): boolean {
  return app.next_renewal_open === true && Array.isArray(grants) && signatureRenewalDue(grants, now);
}

export function deriveRenewalCta(
  app: FamilyApplication | null | undefined,
  grants?: GrantRequest[] | null,
  now?: Date,
): RenewalCtaState {
  // #2659: next year's renewal, beneath a decided one whose grant year is ending.
  if (app?.status && isRenewalDecided(app) && nextRenewalOpen(app, grants, now)) return START;
  switch (app?.status) {
    case 'draft':
      return { kind: 'editable', label: 'Continue your renewal' };
    case 'needs_more_info':
      return { kind: 'needs_info', label: 'Finish your renewal' };
    case 'submitted':
    case 'under_review':
      return { kind: 'pending', status: app.status };
    // #1490: renewals now go through the committee, so the decision states
    // are reachable. Before, each fell to the default below and invited a
    // family whose renewal was in front of the committee to "Start your
    // renewal".
    //
    // In front of the committee: not family-visible as itself (#998 — no
    // decision word before it is final), so it borrows `under_review`, the
    // rule `familyVisibleStatus` applies to a new application.
    case 'staff_recommended':
      return { kind: 'pending', status: 'under_review' };
    // DECISIONS 2026-10-11: the family projection reveals `waitlisted` only
    // once the waitlist notice is sent (until then it reads `under_review`
    // and lands above), so a waitlisted renewal here has been told by email.
    // DECISIONS 2026-10-16: the card names no outcome and no review date.
    case 'waitlisted':
      return { kind: 'waitlisted' };
    // Victor, 2026-10-05: approved is a silent hand-off. Until the approval's
    // grant request exists the family still reads "under review"; once it
    // exists the card steps aside and the grant's own status speaks.
    case 'committee_approved':
      return app.has_grant_request ? { kind: 'handed_off' } : { kind: 'pending', status: 'under_review' };
    // DECISIONS 2026-10-11: said plainly, no button.
    case 'staff_declined':
      return { kind: 'declined' };
    default:
      // No renewal yet: a fresh start is the honest offer.
      if (!app?.status) return START;
      // #2533: any other status (approved, denied, withdrawn, the offer
      // chain) means the renewal was already sent and decided. Inviting
      // "Start your renewal" beneath it was wrong — POST /renewals hands back
      // this same record until the next grant year is due (#2659, above).
      // The invitation steps aside, as it does for a handed-off approval.
      return { kind: 'handed_off' };
  }
}

/**
 * Classify a failed renewals.getMine. 404 means "no renewal yet" — the start
 * invite is correct. Anything else means we do NOT know the family's renewal
 * state, and a surface must not invite a duplicate start (or claim there is
 * nothing) on the strength of a failed request.
 */
export function renewalCtaFromError(err: unknown): RenewalCtaState {
  const status = (err as { response?: { status?: number } } | undefined)?.response?.status;
  return status === 404 ? START : { kind: 'unknown' };
}

/**
 * The copy slots behind the two pending-status lines (#1585 / B-573). This lib
 * is the single source every renewal surface derives from, so the slot KEYS
 * live here once rather than once per surface. The lib stays pure — no hook
 * imports (the CopySlotKey import above is type-only, erased at compile) — and
 * the literals below remain the seed defaults, which copySlots.verify.test.ts
 * pins byte-for-byte against the registry. A component resolves the slots
 * through useCopy() and passes its copy() in as `resolve`.
 */
export const RENEWAL_STATUS_SLOTS = {
  submitted: 'apply.renewal.status_submitted',
  under_review: 'apply.renewal.status_under_review',
} as const satisfies Record<string, CopySlotKey>;

export type RenewalStatusSlotKey =
  (typeof RENEWAL_STATUS_SLOTS)[keyof typeof RENEWAL_STATUS_SLOTS];

const PENDING_LABEL: Record<string, string> = {
  submitted: 'Submitted — your TRF team is reviewing it',
  under_review: 'Under review',
};

/**
 * The status line a family reads on a pending renewal. With `resolve` (the
 * component layer's copy()), TRF's published wording wins; without it — or for
 * a status outside the slotted pair — the seed defaults above hold, so the
 * worst case is the wording this module always carried.
 */
export function pendingRenewalLabel(
  status: string,
  resolve?: (slot: RenewalStatusSlotKey) => string,
): string {
  const slot = RENEWAL_STATUS_SLOTS[status as keyof typeof RENEWAL_STATUS_SLOTS];
  if (slot && resolve) return resolve(slot);
  return PENDING_LABEL[status] ?? status;
}

/** Named so consumers annotate with the TYPE, not the literals — a covered
 *  surface that spells 'Year 2' in an annotation reads as hardcoded wording to
 *  the copy-coverage extractor (it walks literal type nodes too). */
export type RenewalYearLabel = 'Year 2' | 'Year 3';

const YEAR_LABEL: Record<string, RenewalYearLabel> = {
  renewal_year_2: 'Year 2',
  renewal_year_3: 'Year 3',
};

/** 'renewal_year_2' → 'Year 2'; anything non-renewal → null (name no year). */
export function renewalYearLabel(applicationType: unknown): RenewalYearLabel | null {
  return typeof applicationType === 'string' ? (YEAR_LABEL[applicationType] ?? null) : null;
}

/** Year label straight off a renewal application record's intake section. */
export function renewalYearFromApp(app: FamilyApplication | null | undefined): RenewalYearLabel | null {
  const intake = app?.intake as Record<string, unknown> | undefined;
  return renewalYearLabel(intake?.application_type);
}

// ── The year as a descriptor (#2109 R3-B3) ──────────────────────────────────
// `RenewalYearLabel` above is the English form. It is an IDENTIFIER now, not
// display text: every family surface renders the descriptor below (one key,
// "Year {{n}}", so the number and the word order are each language's own).
// Known English readers as of R3 L4: the staff copy preview (English by
// design, R4) and the renewal wizard's callout (ApplicationWizard, whose whole
// sentence is uncatalogued English, an R3 follow-up). The home noticing lane
// switches to `renewalYearMsgFromApp` when L1 and L4 integrate.

const YEAR_NUMBER: Record<string, 2 | 3> = { renewal_year_2: 2, renewal_year_3: 3 };

/** 'renewal_year_2' → 2; anything non-renewal → null. */
export function renewalYearNumber(applicationType: unknown): 2 | 3 | null {
  return typeof applicationType === 'string' ? (YEAR_NUMBER[applicationType] ?? null) : null;
}

/** The year as `grants:renewal.year` with `{n}`; null when no year is known. */
export function renewalYearMsgFromApp(app: FamilyApplication | null | undefined): Msg | null {
  const intake = app?.intake as Record<string, unknown> | undefined;
  const n = renewalYearNumber(intake?.application_type);
  return n == null ? null : msg('grants:renewal.year', { n });
}

/** The descriptor for an English `RenewalYearLabel` (the copy editor's preview passes one). */
export function renewalYearMsgFromLabel(label: RenewalYearLabel | null | undefined): Msg | null {
  if (label === 'Year 2') return msg('grants:renewal.year', { n: 2 });
  if (label === 'Year 3') return msg('grants:renewal.year', { n: 3 });
  return null;
}
